/**
 * todo - resolve typing issues in another story
 */
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, shallowRef, nextTick } from 'vue';
import { thousandsSeparator } from '~/utils/thousandsSeparator';

import parseProductReviewsData from '~/modules/reviews/mappers/parseProductReviewsData';
import type { ReviewsContextInitPayload, ReviewsWithPhotos } from '~/modules/reviews/types';

import useProductStore from '~/stores/useProductStore';
import useReviewsPhotosStore from './useReviewsPhotosStore';
import useReviewsPaginationStore from './useReviewsPaginationStore';
import useReviewsStatisticsStore from './useReviewsStatisticsStore';
import useReviewsSummaryStore from './useReviewsSummaryStore';
import useReviewsServiceStore from './useReviewsServiceStore';
import type { Review } from '~~/shared/globalTypes/reviews';
import type { MemberProductMapProduct } from '~~/shared/globalTypes/product';

import {
  SORT_BY_OPTION,
  ALL_STAR_RATINGS_VALUE,
  ONE_STAR_RATING,
  LOADING_STATE,
  SORT_BY_OPTIONS,
  INITIAL_PAGE_LIMIT,
  SUBSEQUENT_PAGE_LIMIT,
  SORT_BY_OPTIONS_V2,
} from '~/modules/reviews/constants/reviews';

import usePageDataStore from '~/stores/usePageData';

import type { PageData } from '~~/server/pageData';

import { reviewsImgBaseUrl } from 'config';

const getReviewsFlags = (productId: number, pageData: PageData) => {
  const flags = pageData.flags?.groupByProducts?.[productId] || {
    productFlags: {
      isConsolidatedThirdVariantFlag: false,
      isShopTheSetFlag: false,
    },
  };

  return {
    isConsolidatedThirdVariantFlag: flags.productFlags.isConsolidatedThirdVariantFlag,
    isShopTheSetFlag: flags.productFlags.isShopTheSetFlag,
  };
};

const useReviewsStore = defineStore('reviewsStore', () => {
  const { getAllMembers, product } = storeToRefs(useProductStore());
  const { pageData } = storeToRefs(usePageDataStore());
  const productId = product.value.id;
  const { isConsolidatedThirdVariantFlag, isShopTheSetFlag } = getReviewsFlags(productId, pageData.value);

  const reviewsContextProduct = shallowRef<Product | null>(null);
  const reviewsContextMembers = shallowRef<Record<string, Product | MemberProductMapProduct> | null>(null);
  const activeReviewProductId = ref<string | null>(null);

  const { updatePhotos, resetPhotos } = useReviewsPhotosStore();
  const { reviewRequestService } = useReviewsServiceStore();
  const { page } = storeToRefs(useReviewsPaginationStore());
  const { statistics } = storeToRefs(useReviewsStatisticsStore());
  const { generatedSummary } = storeToRefs(useReviewsSummaryStore());

  // State
  const totalResults = ref<number | null>(null);
  const currentResults = ref<number | null>(null);
  const reviews = shallowRef<Review[]>([]);
  const starFiltering = ref<number | typeof ALL_STAR_RATINGS_VALUE>(ALL_STAR_RATINGS_VALUE);
  const sortBy = ref(SORT_BY_OPTION.NEWEST);
  const searchQuery = ref('');
  const loadingState = ref<keyof typeof LOADING_STATE>(LOADING_STATE.LOADED);
  const reviewsHeader = ref('');
  const isFiltered = ref(false);

  // Getters
  /** @type {import('vue').ComputedRef<Nullable<boolean>>} */
  const hasNoReviews = computed(() => totalResults.value === 0);
  const relatedProducts = computed(() => {
    const masterProductId = reviewsContextProduct.value?.id ?? productId;
    const members = reviewsContextMembers.value ?? getAllMembers.value;
    const productBase = members?.[masterProductId];
    return (
      productBase?.productReviews?.reduce(
        (acc, productReview) => ({
          ...acc,
          [productReview?.productId]: {
            ...productReview,
            imageUrl: `${reviewsImgBaseUrl}${productReview?.imageUrl}?bgc=255,255,255&wid=134&qlt=90,0&layer=comp&op_sharpen=0&resMode=bicub&op_usm=0.7,1.0,0.5,0&fmt=jpeg`,
          },
        }),
        {},
      ) ?? {}
    );
  });

  // Actions
  function getOffsetAndLimit() {
    const pageNumber = page.value;
    return {
      limit: pageNumber === 1 ? INITIAL_PAGE_LIMIT : SUBSEQUENT_PAGE_LIMIT,
      offset: pageNumber === 1 ? 0 : (pageNumber - 2) * SUBSEQUENT_PAGE_LIMIT + INITIAL_PAGE_LIMIT,
    };
  }

  function buildReviewsGETParams() {
    const _sortBy = sortBy.value;
    const _searchQuery = searchQuery.value;
    const _starFiltering = starFiltering.value;

    return {
      // sorting
      ...(!!_sortBy && { sort: SORT_BY_OPTIONS_V2[_sortBy] }),
      // search query
      ...(!!_searchQuery && { text: _searchQuery }),
      // filtering
      ...(_starFiltering !== ALL_STAR_RATINGS_VALUE && {
        filter: `rating:${_starFiltering}`,
      }),
      ...getOffsetAndLimit(),
    };
  }
  function updateHeader() {
    let result = `${currentResults.value ? thousandsSeparator(currentResults.value) : 'No'} review`;
    result += currentResults.value === 1 ? '' : 's';

    if (starFiltering.value !== ALL_STAR_RATINGS_VALUE) {
      result += ` with ${starFiltering.value} ${starFiltering.value === ONE_STAR_RATING ? 'star' : 'stars'}`;
    }
    if (searchQuery.value) {
      result += ` mentioning "${searchQuery.value}"`;
    }
    result += `<span style="font-size:0">sorted by ${SORT_BY_OPTIONS.find((option) => option.value === sortBy.value)?.displayValue}</span>`;
    // code in order sortBy change is announced by screenreader - font-size:0 hides it but content is being read by screenreader
    reviewsHeader.value = result;
  }

  function updateIsFiltered() {
    isFiltered.value = starFiltering.value !== ALL_STAR_RATINGS_VALUE || !!searchQuery.value;
  }

  /** One-time seam init: product payload and members for parse/init/fetch. */
  function initReviewsContext(payload: ReviewsContextInitPayload) {
    reviewsContextProduct.value = payload.product;
    reviewsContextMembers.value = payload.members;
    setActiveReviewProductId(payload.activeReviewProductId);
  }

  /**
   * Sets the active review product id. First bind sets id only; member change resets photos/filters and re-inits.
   */
  function setActiveReviewProductId(id: string | null): void {
    if (!id) return;

    const previousId = activeReviewProductId.value;
    if (previousId === id) return;

    activeReviewProductId.value = id;

    if (previousId == null) return;

    resetPhotos();
    resetFilters();
    void initReviews();
  }

  /** Initializes the reviews store with data from the product payload. */
  async function initReviews(productIdOverride: string | null = null) {
    const contextProduct = reviewsContextProduct.value ?? product.value;
    const reviewProductId = productIdOverride ?? activeReviewProductId.value;

    const {
      reviews: parsedReviews,
      totalResults: parsedTotalResults,
      reviewStatistics,
      generatedSummary: parsedGeneratedSummary,
      reviewsWithPhotos,
    } = parseProductReviewsData(
      {
        productId: reviewProductId,
        product: contextProduct,
      },
      !!pageData.value.killswitches?.reviewVideoUploadEnabled,
    );

    reviews.value = parsedReviews;
    if (reviewsWithPhotos?.photos?.length) {
      updatePhotos(reviewsWithPhotos as ReviewsWithPhotos);
    }
    generatedSummary.value = parsedGeneratedSummary;
    statistics.value = reviewStatistics;
    totalResults.value = parsedTotalResults;
    currentResults.value = parsedTotalResults;
    updateHeader();
  }

  /** Fetches reviews from the API based on current filters, sorting, and pagination. */
  async function fetchReviews() {
    loadingState.value = LOADING_STATE.LOADING;
    const id = activeReviewProductId.value;

    return reviewRequestService({
      productId: id,
      params: buildReviewsGETParams(),
    })
      .then((response) => {
        const {
          reviews: parsedReviews,
          totalResults: parsedCurrentResults, // this value is relative to the data returned in the response
        } = parseProductReviewsData(
          {
            product: 'data' in response ? response.data : response,
            productId: id,
          },
          !!pageData.value.killswitches?.reviewVideoUploadEnabled,
        );

        if (totalResults.value === null || isConsolidatedThirdVariantFlag || isShopTheSetFlag) {
          totalResults.value = parsedCurrentResults || 0;
        }
        reviews.value = parsedReviews;
        currentResults.value = parsedCurrentResults;
        loadingState.value = LOADING_STATE.LOADED;
        updateHeader();
        updateIsFiltered();
      })
      .catch((error: Error) => {
        console.error('Could not fetch reviews: ', error);
        loadingState.value = LOADING_STATE.ERROR;
      });
  }

  function getStarFilterRating(starRating: number | string | typeof ALL_STAR_RATINGS_VALUE): number | typeof ALL_STAR_RATINGS_VALUE {
    if (starRating === ALL_STAR_RATINGS_VALUE) {
      return starRating;
    }
    return typeof starRating !== 'number' ? parseInt(starRating, 10) : starRating;
  }

  /** Handles changes to the star rating filter. */
  function handleFilterByChange(starRating: number | string | typeof ALL_STAR_RATINGS_VALUE) {
    page.value = 1;
    starFiltering.value = getStarFilterRating(starRating);
    document.getElementById('reviewFilterBy')?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  /** Handles changes to the sort by option. */
  function handleSortByChange(newSortBy: string) {
    page.value = 1;
    sortBy.value = newSortBy;
    updateHeader();
    nextTick(() => {
      document.getElementById('reviewFilterBy')?.scrollIntoView({
        behavior: 'smooth',
      });
    });
  }

  /** Handles changes to the search query. */
  function handleSearchQueryChange(newSearchQuery: string) {
    page.value = 1;
    searchQuery.value = newSearchQuery;
  }

  /** Resets all review filters to their default values. */
  function resetFilters() {
    page.value = 1;
    searchQuery.value = '';
    starFiltering.value = ALL_STAR_RATINGS_VALUE;
    sortBy.value = SORT_BY_OPTION.NEWEST;
  }
  return {
    // State
    activeReviewProductId,
    totalResults,
    currentResults,
    reviews,
    starFiltering,
    sortBy,
    searchQuery,
    loadingState,
    reviewsHeader,
    isFiltered,

    // Getters
    hasNoReviews,
    relatedProducts,

    // Actions
    updateHeader,
    updateIsFiltered,
    fetchReviews,
    handleFilterByChange,
    handleSortByChange,
    handleSearchQueryChange,
    resetFilters,
    initReviewsContext,
    setActiveReviewProductId,
    initReviews,
  };
});

export default useReviewsStore;
